import { useRef, useState } from "react";
import { CalendarClock, Check, ChevronDown, Database, Download, Save } from "lucide-react";
import { userReportsApi } from "../../api";
import type { GeneratedReportProposal } from "../../api/userReports";
import { useClickOutside } from "../../hooks";
import { downloadCsv, downloadJson } from "../../utils";
import ReportFormModal from "../Reports/ReportFormModal";
import ScheduleModal from "../Reports/ScheduleModal";
import MessageFeedbackAction from "./MessageFeedbackAction";
import type { UserReport } from "../../types/reports";

type SqlActionsProps = {
  sql: string;
  messageId?: string | null;
  question?: string;
};

export default function SqlActions({ sql, messageId, question }: SqlActionsProps) {
  const [showSql, setShowSql] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedReport, setSavedReport] = useState<UserReport | null>(null);
  const [scheduleReport, setScheduleReport] = useState<UserReport | null>(null);

  const trimmedQuestion = (question ?? "").trim();
  const prefill: GeneratedReportProposal = {
    name: trimmedQuestion ? trimmedQuestion.slice(0, 80) : "Report from chat",
    description: trimmedQuestion || null,
    sql_query: sql,
    is_public: false,
  };
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(downloadMenuRef, () => setDownloadOpen(false), downloadOpen);

  async function handleDownload(format: "csv" | "json") {
    setDownloadOpen(false);
    setDownloadLoading(true);

    try {
      const response = await userReportsApi.runSql(sql);
      const rows = response.data?.rows || [];

      if (format === "csv") downloadCsv(rows, "query_result");
      else downloadJson(rows, "query_result");
    } catch { return; } finally {
      setDownloadLoading(false);
    }
  }

  return (
    <div className="mt-2 text-xs text-gray-400">
      <div className="flex items-center gap-3">
        <div className="relative" ref={downloadMenuRef}>
          <button
            onClick={() => setDownloadOpen((isOpen) => !isOpen)}
            disabled={downloadLoading}
            className="flex items-center gap-1 hover:text-gray-600 disabled:opacity-50"
          >
            <Download size={11} />
            <span>{downloadLoading ? "Downloading..." : "Download"}</span>
            <ChevronDown size={10} />
          </button>
          {downloadOpen && (
            <div className="absolute left-0 top-full z-10 mt-1 w-24 rounded-lg border border-gray-200 bg-white shadow-md">
              <button
                onClick={() => handleDownload("csv")}
                className="flex w-full items-center rounded-t-lg px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
              >
                CSV
              </button>
              <button
                onClick={() => handleDownload("json")}
                className="flex w-full items-center rounded-b-lg px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
              >
                JSON
              </button>
            </div>
          )}
        </div>

        <button
          onClick={() => setSaving(true)}
          className="flex items-center gap-1 hover:text-gray-600"
        >
          <Save size={11} />
          <span>Save as report</span>
        </button>

        <button
          onClick={() => setShowSql((isShown) => !isShown)}
          className="flex items-center gap-1 hover:text-gray-600"
        >
          <Database size={11} />
          <span>{showSql ? "Hide SQL" : "Show SQL"}</span>
        </button>

        <MessageFeedbackAction messageId={messageId} />
      </div>

      {savedReport && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 text-green-700">
          <Check size={12} aria-hidden="true" />
          <span>Saved to Reports.</span>
          <button
            onClick={() => { setScheduleReport(savedReport); setSavedReport(null); }}
            className="flex items-center gap-1 font-medium underline hover:text-green-800"
          >
            <CalendarClock size={11} aria-hidden="true" />
            Schedule it
          </button>
          <button
            onClick={() => setSavedReport(null)}
            className="ml-auto text-green-600 hover:text-green-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {showSql && (
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded border border-blue-200 bg-blue-50 px-3 py-2 text-gray-700">
          {sql}
        </pre>
      )}

      {saving && (
        <ReportFormModal
          prefill={prefill}
          onClose={() => setSaving(false)}
          onSuccess={(saved) => {
            setSaving(false);
            if (saved) setSavedReport(saved);
          }}
        />
      )}

      {scheduleReport && (
        <ScheduleModal
          report={scheduleReport}
          onClose={() => setScheduleReport(null)}
          onSuccess={() => setScheduleReport(null)}
        />
      )}
    </div>
  );
}
